/**
 * Authentication Gate
 * Protects routes that require authentication
 * Uses server-side session cookies validated via AppContext
 *
 * Also handles onboarding redirect for new accounts
 *
 * Redirect priority when not authenticated after initialization (RP-2567):
 *  1. requiresResovaLogin=true && VITE_RESOVA_LOGIN_URL set → hard-redirect to Resova login
 *  2. fallback                                              → SPA-navigate to /signin
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useAccount } from '@/context';

interface AuthGateProps {
  children: React.ReactNode;
  /** Skip onboarding check (used for /onboarding route itself) */
  skipOnboardingCheck?: boolean;
}

export function AuthGate({ children, skipOnboardingCheck = false }: AuthGateProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isInitializing, requiresResovaLogin } = useAuth();
  const { account } = useAccount();

  useEffect(() => {
    if (isInitializing) return;

    if (!isAuthenticated) {
      const resovaLoginUrl = import.meta.env.VITE_RESOVA_LOGIN_URL;

      if (requiresResovaLogin && resovaLoginUrl) {
        window.location.href = resovaLoginUrl;
      } else {
        navigate('/signin');
      }
      return;
    }

    // Check if account needs onboarding
    // Skip check if we're on the onboarding page or if explicitly skipped
    const isOnOnboardingPage = location.pathname === '/onboarding';
    if (!skipOnboardingCheck && !isOnOnboardingPage && account && !account.onboardingCompletedAt) {
      navigate('/onboarding');
    }
  }, [isAuthenticated, isInitializing, requiresResovaLogin, navigate, account, skipOnboardingCheck, location.pathname]);

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
